/** @file The session's status under the form: running, failed (retry), plan ready, applied (with Undo). */
import type { RevertLink } from '@pose2d/api/queries/undoneRevs';
import type { Translate } from '@pose2d/i18n/types';
import { errorText } from '../../shell/i18n/errorText';
import { Button } from '../../ui-kit/Button';
import { appliedUndone } from './appliedUndone';
import type { AppliedView, AutorigSession, AutorigView } from './autorigTypes';
import { confidenceBand } from './confidenceBand';

/** Props of SessionStatus. */
export interface SessionStatusProps {
  t: Translate;
  view: AutorigView;
  session: Pick<AutorigSession, 'run' | 'undo'>;
  /** The commit feed (liveStore.commits): a Mod+Z or redo of the applied rev shows on the banner. */
  commits?: readonly RevertLink[];
}

/** Undo is offered only while neither the feed nor the session's own Undo has undone the plan. */
function Applied({ t, view, session, commits }: { t: Translate; view: AppliedView; session: Pick<AutorigSession, 'undo'>; commits: readonly RevertLink[] }) {
  const { percent } = confidenceBand(view.confidence);
  const undone = appliedUndone(view, commits);
  return (
    <div className="autorig-applied" role="status">
      <p>{undone ? t('autorig.undone', { rev: view.rev }) : t('autorig.applied', { rev: view.rev, percent })}</p>
      {undone || view.undone ? null : <Button label={t('common.undo')} disabled={view.undoing} onClick={() => { void session.undo(); }} />}
      {view.failure === null ? null : <p role="alert" className="shell-error">{errorText(t, view.failure.thrown)}</p>}
    </div>
  );
}

/** Idle shows nothing; the review itself is the ReviewDialog. */
export function SessionStatus({ t, view, session, commits = [] }: SessionStatusProps) {
  switch (view.kind) {
    case 'idle':
      return null;
    case 'running':
      return <p className="autorig-note" role="status">{t('autorig.running', { fn: view.request.fn })}</p>;
    case 'failed':
      return (
        <div role="alert" className="shell-error">
          <p>{t('autorig.failed')}</p>
          <p>{errorText(t, view.thrown)}</p>
          <Button label={t('autorig.rerun')} onClick={() => { void session.run(view.request); }} />
        </div>
      );
    case 'review':
      return <p className="autorig-note">{t('autorig.ready', { commands: view.reply.plan.commands.length, percent: confidenceBand(view.reply.plan.confidence).percent })}</p>;
    case 'applied':
      return <Applied t={t} view={view} session={session} commits={commits} />;
  }
}
